import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { path } from "./therapyPaths";
import TherapyNav from "./TherapyNav";
import TherapyFooter from "./TherapyFooter";
import { Icon } from "./Icons";
import "./Therapy.css";
import "./TherapyPages.css";
import "./Gallery.css";

/**
 * Gallery images.
 *
 * waiting-room, main-office, couples-room, group-room,
 * consultation-corner, and back-garden use local practice photos
 * from /public/images/therapypractice/.
 *
 * neighborhood and library still use Unsplash placeholders until
 * real photos are provided for those two.
 *
 * If a specific photo fails to load, the <img> onError handler swaps
 * in an SVG fallback so the UI doesn't show a broken-image icon.
 */
const GALLERY_ITEMS = [
    {
        id: "waiting-room",
        caption: "The waiting area",
        description:
            "Quiet, with natural light. Tea, water, and somewhere to sit before your session.",
        url: "/images/therapypractice/waitingroom.jpg",
        urlSmall: "/images/therapypractice/waitingroom.jpg",
        fallbackColors: ["#E8EFE9", "#C9A77C"],
    },
    {
        id: "main-office",
        caption: "Main therapy office",
        description:
            "Where most individual sessions happen. Two chairs, a couch, and a window facing east.",
        url: "/images/therapypractice/main.jpg",
        urlSmall: "/images/therapypractice/main.jpg",
        fallbackColors: ["#4A6B5C", "#E8D5B7"],
    },
    {
        id: "couples-room",
        caption: "Couples therapy room",
        description:
            "A larger space designed for two clients and a clinician. More room to spread out.",
        url: "/images/therapypractice/couples.jpg",
        urlSmall: "/images/therapypractice/couples.jpg",
        fallbackColors: ["#5B7B8C", "#D4B896"],
    },
    {
        id: "neighborhood",
        caption: "Brooklyn Heights",
        description:
            "Two blocks from Borough Hall and the Promenade. Easy access from Manhattan and the rest of Brooklyn.",
        // Brownstone street photo — replaced from prior URL that didn't render
        url: "https://images.unsplash.com/photo-1485871981521-5b1fd3805eee?w=1200&q=80&auto=format&fit=crop",
        urlSmall: "https://images.unsplash.com/photo-1485871981521-5b1fd3805eee?w=600&q=75&auto=format&fit=crop",
        fallbackColors: ["#7A5C8E", "#F0C987"],
    },
    {
        id: "group-room",
        caption: "Group room",
        description:
            "Used for occasional group offerings — currently a perinatal support group and a young-professionals burnout circle.",
        url: "/images/therapypractice/group.avif",
        urlSmall: "/images/therapypractice/group.avif",
        fallbackColors: ["#6B8E5A", "#D4A574"],
    },
    {
        id: "library",
        caption: "Resource library",
        description:
            "A small library of clinician-recommended books and worksheets. Borrow anything; no checkout needed.",
        url: "https://images.unsplash.com/photo-1507842217343-583bb7270b66?w=1200&q=80&auto=format&fit=crop",
        urlSmall: "https://images.unsplash.com/photo-1507842217343-583bb7270b66?w=600&q=75&auto=format&fit=crop",
        fallbackColors: ["#8B6F47", "#E8D5B7"],
    },
    {
        id: "consultation-corner",
        caption: "Consultation corner",
        description:
            "Where free 20-minute consultation calls happen. Phone-only, by design.",
        url: "/images/therapypractice/corner.jpg",
        urlSmall: "/images/therapypractice/corner.jpg",
        fallbackColors: ["#3D5A6C", "#B8956A"],
    },
    {
        id: "back-garden",
        caption: "The back garden",
        description:
            "A small private outdoor space. Some clinicians offer walking sessions when weather permits.",
        url: "/images/therapypractice/garden.jpg",
        urlSmall: "/images/therapypractice/garden.jpg",
        fallbackColors: ["#6B8E5A", "#E8EFE9"],
    },
];

/**
 * Gallery image with graceful fallback.
 * If the real image fails to load, swaps to an SVG gradient
 * with the item's caption.
 */
function GalleryImage({ item, large = false }) {
    const [errored, setErrored] = useState(false);
    const src = large ? item.url : item.urlSmall;

    if (errored) {
        const [c1, c2] = item.fallbackColors;
        return (
            <svg
                viewBox="0 0 300 200"
                xmlns="http://www.w3.org/2000/svg"
                style={{ width: "100%", height: "100%", display: "block" }}
            >
                <defs>
                    <linearGradient id={`fb-${item.id}`} x1="0" y1="0" x2="1" y2="1">
                        <stop offset="0%" stopColor={c1} />
                        <stop offset="100%" stopColor={c2} />
                    </linearGradient>
                </defs>
                <rect width="300" height="200" fill={`url(#fb-${item.id})`} />
                <text
                    x="150"
                    y="105"
                    textAnchor="middle"
                    fill="white"
                    fontSize="14"
                    fontFamily="Inter, sans-serif"
                    opacity="0.85"
                >
                    {item.caption}
                </text>
            </svg>
        );
    }

    return (
        <img
            src={src}
            alt={item.caption}
            loading="lazy"
            onError={() => setErrored(true)}
        />
    );
}

function TherapyPractice() {
    const [lightbox, setLightbox] = useState(null);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        if (lightbox) {
            document.body.style.overflow = "hidden";
            return () => {
                document.body.style.overflow = "";
            };
        }
    }, [lightbox]);

    return (
        <main className="demo demo-therapy">
            <TherapyNav />

            <section className="page-header">
                <div className="demo-container">
                    <p className="demo-eyebrow">The practice</p>
                    <h1>Where the work happens.</h1>
                    <p className="page-lede">
                        Harbor Therapy is a group practice in Brooklyn Heights. Eight
                        clinicians, one shared office, and a commitment to making
                        therapy feel less like a transaction and more like a thing
                        worth showing up for.
                    </p>
                </div>
            </section>

            <section className="demo-section">
                <div className="demo-container">
                    <div className="practice-info-grid">
                        <div className="practice-info-card">
                            <Icon name="pin" className="practice-info-icon" />
                            <h3>Location</h3>
                            <p>
                                142 Montague Street, Suite 4B
                                <br />
                                Brooklyn Heights, NY 11201
                            </p>
                            <p className="practice-info-meta">
                                Two blocks from Court St / Borough Hall (2/3/4/5/R/N).
                                Building has an elevator.
                            </p>
                        </div>
                        <div className="practice-info-card">
                            <Icon name="clock" className="practice-info-icon" />
                            <h3>Hours</h3>
                            <p>
                                Monday – Friday: 9 AM – 6 PM
                                <br />
                                Saturday: 10 AM – 2 PM
                                <br />
                                Sunday: Closed
                            </p>
                            <p className="practice-info-meta">
                                Individual clinicians keep their own schedules within
                                these hours.
                            </p>
                        </div>
                        <div className="practice-info-card">
                            <Icon name="phone" className="practice-info-icon" />
                            <h3>Contact</h3>
                            <p>
                                (718) 555-0142
                                <br />
                                hello@harbortherapy.com
                            </p>
                            <p className="practice-info-meta">
                                Booking and brief logistics. Clinical questions go
                                through the secure portal.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            <section className="demo-section demo-alt">
                <div className="demo-container">
                    <div className="section-header">
                        <p className="demo-eyebrow">A look around</p>
                        <h2>The space.</h2>
                        <p className="section-lede">
                            We've found that knowing what a place looks like before
                            you walk in makes the first session a little less
                            intimidating.
                        </p>
                    </div>

                    <div className="gallery-grid">
                        {GALLERY_ITEMS.map((item) => (
                            <button
                                key={item.id}
                                className="gallery-item"
                                onClick={() => setLightbox(item)}
                                aria-label={`Open larger view of ${item.caption}`}
                            >
                                <div className="gallery-item-img">
                                    <GalleryImage item={item} />
                                </div>
                                <div className="gallery-item-caption">
                                    <h4>{item.caption}</h4>
                                </div>
                            </button>
                        ))}
                    </div>

                </div>
            </section>

            <section className="demo-section">
                <div className="demo-container">
                    <div className="section-header">
                        <p className="demo-eyebrow">What we believe</p>
                        <h2>Some practice principles.</h2>
                    </div>

                    <div className="demo-grid-3 approach-grid">
                        <div className="approach-card">
                            <Icon name="heart" className="approach-icon" />
                            <h3>Match before momentum</h3>
                            <p>
                                We'd rather refer you out in the first session than
                                keep you in a relationship that doesn't fit. The free
                                consultation exists for exactly this reason.
                            </p>
                        </div>
                        <div className="approach-card">
                            <Icon name="layers" className="approach-icon" />
                            <h3>Specialization is a feature</h3>
                            <p>
                                Every clinician here has specific areas they work in.
                                We don't pretend everyone is a generalist who can take
                                anything.
                            </p>
                        </div>
                        <div className="approach-card">
                            <Icon name="shield" className="approach-icon" />
                            <h3>Honest about what we are</h3>
                            <p>
                                We're not a crisis service, not a quick fix, and not a
                                substitute for medication when it's needed. We do the
                                work we're trained for and refer for the rest.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            <section className="demo-section demo-cta">
                <div className="demo-container">
                    <h2>Want to come see it?</h2>
                    <p>
                        Most clients start with a free consultation by phone. If
                        we&rsquo;re a fit and you book in-person sessions, you&rsquo;ll
                        see all of this for yourself.
                    </p>
                    <div className="demo-actions demo-actions-center">
                        <Link to={path("therapists")} className="btn-primary">
                            Browse therapists
                        </Link>
                        <Link to={path("booking")} className="btn-secondary">
                            Free consultation
                        </Link>
                    </div>
                </div>
            </section>

            <TherapyFooter />

            {lightbox && (
                <div
                    className="gallery-lightbox"
                    onClick={() => setLightbox(null)}
                    role="dialog"
                    aria-modal="true"
                >
                    <button
                        className="gallery-lightbox-close"
                        onClick={() => setLightbox(null)}
                        aria-label="Close"
                    >
                        ×
                    </button>
                    <div
                        className="gallery-lightbox-content"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="gallery-lightbox-img">
                            <GalleryImage item={lightbox} large />
                        </div>
                        <div className="gallery-lightbox-caption">
                            <h3>{lightbox.caption}</h3>
                            <p>{lightbox.description}</p>
                        </div>
                    </div>
                </div>
            )}
        </main>
    );
}

export default TherapyPractice;